import React from 'react';
import { DollarSign, Calendar, TrendingUp } from 'lucide-react';
import type { Deal } from './types';

interface DealCardProps {
  deal: Deal;
  onClick?: () => void;
  isDragging?: boolean;
}

const PRIORITY_CONFIG = {
  low:      { label: 'Low',      cls: 'bg-slate-500/15 text-slate-400' },
  medium:   { label: 'Medium',   cls: 'bg-blue-500/15 text-blue-400' },
  high:     { label: 'High',     cls: 'bg-orange-500/15 text-orange-400' },
  critical: { label: 'Critical', cls: 'bg-red-500/15 text-red-400' },
};

export function formatDealValue(value: number, currency = 'USD'): string {
  if (value === 0) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

function formatCloseDate(dateStr?: string): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  const diff = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `Overdue`;
  if (diff === 0) return 'Due today';
  if (diff <= 7) return `${diff}d left`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const DealCard: React.FC<DealCardProps> = ({ deal, onClick, isDragging }) => {
  const priority = PRIORITY_CONFIG[deal.priority] || PRIORITY_CONFIG.medium;
  const closeDate = formatCloseDate(deal.expectedCloseDate);
  const isOverdue = deal.expectedCloseDate
    ? new Date(deal.expectedCloseDate + 'T00:00:00') < new Date()
    : false;

  return (
    <div
      onClick={onClick}
      className={`
        group relative bg-background dark:bg-card border rounded-xl p-3.5 cursor-pointer transition-all duration-150 select-none
        ${isDragging
          ? 'shadow-2xl border-workspace-accent/50 scale-[1.02] rotate-1'
          : 'border-border hover:border-workspace-accent/40 hover:shadow-md'
        }
      `}
    >
      {/* Priority strip */}
      <div className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full ml-0 ${
        deal.priority === 'critical' ? 'bg-red-500' :
        deal.priority === 'high' ? 'bg-orange-400' :
        deal.priority === 'medium' ? 'bg-blue-400' : 'bg-slate-500'
      }`} />

      {/* Title + priority */}
      <div className="flex items-start justify-between gap-2 pl-2">
        <h4 className="text-sm font-semibold text-foreground dealing-snug line-clamp-2 group-hover:text-workspace-accent transition-colors flex-1">
          {deal.title}
        </h4>
        <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${priority.cls}`}>
          {priority.label}
        </span>
      </div>

      {/* Value + probability */}
      <div className="flex items-center gap-3 mt-2.5 pl-2">
        {deal.value > 0 && (
          <div className="flex items-center gap-1 text-sm font-semibold text-emerald-500">
            <DollarSign className="w-3.5 h-3.5" />
            {formatDealValue(deal.value, deal.currency)}
          </div>
        )}
        {deal.probability > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
            <TrendingUp className="w-3 h-3" />
            {deal.probability}%
          </div>
        )}
      </div>

      {/* Close date */}
      {closeDate && (
        <div className="flex items-center mt-2 pl-2">
          <div className={`flex items-center gap-1 text-[11px] font-medium ${isOverdue ? 'text-red-400' : 'text-muted-foreground'}`}>
            <Calendar className="w-3 h-3" />
            {closeDate}
          </div>
        </div>
      )}
    </div>
  );
};
